<template>
  <section>
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <p class="level-item">
          <a class="button is-primary" @click="onAdd">
            <b-icon icon="plus"></b-icon>
            <span>{{ $t('attributes.add') + $t('attributes.question') }}</span>
          </a>
        </p>
      </div>
    </nav>

    <b-table :data="questions" :loading="loading" detailed>
      <b-table-column
        field="sequence"
        :label="$t('attributes.sort')"
        :width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.sequence }}
      </b-table-column>

      <b-table-column
        field="title"
        :label="$t('attributes.question')"
        v-slot="props"
      >
        {{ props.row.title }}
      </b-table-column>

      <b-table-column
        field="note"
        :label="$t('attributes.note')"
        v-slot="props"
      >
        {{ props.row.note }}
      </b-table-column>

      <b-table-column
        field="type"
        :label="$t('attributes.question_type')"
        :width="80"
        v-slot="props"
      >
        {{ typeName(props.row.type) }}
      </b-table-column>

      <b-table-column :width="300" v-slot="props">
        <div class="buttons">
          <b-button
            type="is-primary"
            outlined
            @click.prevent="onModify(props.row)"
          >
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button
            v-if="isSelectType(props.row)"
            type="is-black"
            outlined
            @click.prevent="onEditOptions(props.row)"
          >
            修改選項
          </b-button>
          <b-button
            type="is-danger"
            outlined
            @click.prevent="onDelete(props.row)"
          >
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #detail="props">
        {{ optionList(props.row.options) }}
      </template>
    </b-table>

    <b-modal
      :active.sync="isQuestionFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <common-question-form :question="selectedQuestion" @send="onSend" />
    </b-modal>

    <b-modal
      :active.sync="isOptionsFormModalActive"
      :can-cancel="false"
      has-modal-card
    >
      <common-question-options-form
        :traveler-types="travelerTypes"
        :question="selectedQuestion"
        @send="onSendOptions"
      />
    </b-modal>
  </section>
</template>

<script>
import { TRAVEL_GROUPS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import CommonQuestionForm from './CommonQuestionForm'
import CommonQuestionOptionsForm from './CommonQuestionOptionsForm'

export default {
  name: 'CommonQuestions',
  components: { CommonQuestionForm, CommonQuestionOptionsForm },
  props: {
    travelGroup: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      loading: false,
      sending: false,

      code: this.travelGroup.code,
      travelerTypes: [],
      questions: [],
      total: 0,

      selectedQuestion: null,
      isQuestionFormModalActive: false,
      isOptionsFormModalActive: false
    }
  },
  watch: {
    travelGroup(travelGroup) {
      this.code = travelGroup.code
      this.loadData(travelGroup.code)
    }
  },
  mounted() {
    this.loadData(this.travelGroup.code)
  },
  methods: {
    async loadData(code) {
      this.loading = true
      await this.loadTravelerTypesAsync(code)
      await this.loadQuestionsAsync(code)
      this.loading = false
    },
    async loadTravelerTypesAsync(code) {
      try {
        const api = `${TRAVEL_GROUPS_API}/${code}/traveler-types`
        const res = await this.axios.get(api)
        const travelerTypes = res.data.data
        this.travelerTypes = travelerTypes.sort((t1, t2) => t1.sort - t2.sort)
      } catch (e) {
        this.travelerTypes = []

        notifyService.fetch_error('traveler_type', e)
      }
    },
    async loadQuestionsAsync(code) {
      try {
        const api = `${TRAVEL_GROUPS_API}/${code}/common-questions`
        const res = await this.axios.get(api)
        this.questions = res.data.data
      } catch (e) {
        this.questions = []

        notifyService.fetch_error('common_question', e)
      }
    },
    onAdd() {
      this.selectedQuestion = {}
      this.isQuestionFormModalActive = true
    },
    onModify(item) {
      this.selectedQuestion = item
      this.isQuestionFormModalActive = true
    },
    onSend(item) {
      if (!item.id) {
        this.createQuestion(item)
      } else {
        this.updateQuestion(item)
      }
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteQuestion(item)
        }
      })
    },
    onEditOptions(item) {
      this.selectedQuestion = item
      this.isOptionsFormModalActive = true
    },
    onSendOptions(options) {
      this.updateQuestionOptions(options)
    },
    createQuestion(item) {
      const api = `${TRAVEL_GROUPS_API}/${this.code}/common-questions`
      const config = {
        errorHandle: {
          customPrefix: `${this.$t('attributes.add_failed')}: `
        }
      }

      this.sending = true
      this.axios
        .post(api, item, config)
        .then(res => {
          const _item = res.data.data

          const questions = [...this.questions, _item]
          this.questions = questions.sort((a, b) => a.sequence - b.sequence)

          this.isQuestionFormModalActive = false

          notifyService.success(`${this.$t('attributes.add_succeed')}`)
        })
        .finally(() => {
          this.sending = false
        })
    },
    updateQuestion(item) {
      const api = `${TRAVEL_GROUPS_API}/common-questions/${item.id}`
      const config = {
        errorHandle: {
          customPrefix: `${this.$t('attributes.update_failed')}: `
        }
      }

      this.sending = true
      this.axios
        .put(api, item, config)
        .then(res => {
          const _item = res.data.data

          const idx = this.questions.findIndex(s => s.id === _item.id)
          const questions = [...this.questions]
          questions[idx] = _item
          this.selectedQuestion = _item
          this.questions = questions.sort((a, b) => a.sequence - b.sequence)

          this.isQuestionFormModalActive = false

          notifyService.success(`${this.$t('attributes.update_succeed')}`)
        })
        .finally(() => {
          this.sending = false
        })
    },
    updateQuestionOptions(options) {
      const api = `${TRAVEL_GROUPS_API}/common-questions/${this.selectedQuestion.id}/options`
      const config = {
        errorHandle: {
          customPrefix: `${this.$t('attributes.update_failed')}: `
        }
      }

      const item = {
        options: options
      }

      this.sending = true
      this.axios
        .put(api, item, config)
        .then(res => {
          const _item = res.data.data

          const idx = this.questions.findIndex(s => s.id === _item.id)
          const questions = [...this.questions]
          questions[idx] = _item
          this.selectedQuestion = _item
          this.questions = questions.sort((a, b) => a.sequence - b.sequence)

          notifyService.success(`${this.$t('attributes.update_succeed')}`)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error)
        })
        .finally(() => {
          this.sending = false
        })
    },
    deleteQuestion(item) {
      const api = `${TRAVEL_GROUPS_API}/common-questions/${item.id}`
      const config = {
        errorHandle: {
          customPrefix: `${this.$t('attributes.delete_failed')}: `
        }
      }

      this.loading = true
      return this.axios
        .delete(api, config)
        .then(() => {
          this.questions = this.questions.filter(i => i.id !== item.id)
          notifyService.success(`${this.$t('attributes.delete_succeed')}`)
        })
        .finally(() => {
          this.loading = false
        })
    },
    isSelectType(item) {
      return item.type === 0 || item.type === 1
    },
    typeName(type) {
      let name = ''

      switch (type) {
        case 0:
          name = '單選'
          break
        case 1:
          name = '多選'
          break
        case 2:
          name = '填充'
          break
        case 3:
          name = '日期'
          break
        case 4:
          name = '圖檔'
          break
        default:
          name = '題型錯誤'
          break
      }

      return name
    },
    optionList(options) {
      return options.map(o => o.name).join(' / ')
    }
  }
}
</script>

<style lang="scss" scoped></style>
